import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Bell, LogOut, MessageCircle, UserRound } from 'lucide-react';

import { CoreValueTicker } from '@/components/comms/CoreValueTicker';
import { NotificationInbox } from '@/components/notifications/NotificationInbox';
import { useAuth } from '@/hooks/useAuth';
import { useCommsUnread } from '@/hooks/useCommsUnread';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';
import { OrgSwitcher } from './OrgSwitcher';

/**
 * Desktop header. Hidden on mobile (the MobileHeader is used instead).
 * Shows the current user's initials + a click-to-open menu with sign-out.
 *
 * The bell opens the notification inbox popover (ADR-048 mounted it — the
 * inbox existed but nothing rendered it, so every notification kind was
 * reachable only by typing /notifications). Announcements keep their own
 * link inside the panel (ADR-027 decision 10).
 */
export function Header() {
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inboxRef = useRef<HTMLDivElement>(null);

  const unread = useCommsUnread().data;
  const chatUnread = unread?.chat.totalUnread ?? 0;
  const announcementUnread =
    (unread?.announcements.unread ?? 0) + (unread?.announcements.unacknowledged ?? 0);
  const notificationUnread = useUnreadNotificationCount().data ?? 0;
  const bellUnread = announcementUnread + notificationUnread;

  // Click-outside-to-close (account menu + inbox popover)
  useEffect(() => {
    if (!menuOpen && !inboxOpen) return undefined;
    function handler(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (inboxRef.current && !inboxRef.current.contains(e.target as Node)) {
        setInboxOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [menuOpen, inboxOpen]);

  const initials =
    state.status === 'authenticated'
      ? `${state.me.user.firstName.charAt(0)}${state.me.user.lastName.charAt(0)}`.toUpperCase()
      : '·';

  return (
    <header className="hidden h-16 border-b border-kw-border bg-white md:flex md:items-center md:gap-4 md:px-6">
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent('cmdk:open'))}
        className="flex h-9 w-full max-w-md items-center gap-2 rounded-kw-md border border-kw-border bg-kw-bg px-3 text-left text-[13px] text-kw-muted hover:border-kw-cerulean/30"
      >
        <Search className="size-4" />
        <span className="flex-1">Search anything…</span>
        <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-kw-muted">⌘K</kbd>
      </button>

      {/* Rotating core value — sits between search and the right cluster. */}
      <CoreValueTicker className="mx-2 hidden min-w-0 flex-1 lg:flex" />

      <div className="ml-auto flex items-center gap-3">
        <button
          type="button"
          aria-label={chatUnread > 0 ? `Messages, ${String(chatUnread)} unread` : 'Messages'}
          onClick={() => {
            void navigate('/messages');
          }}
          className="relative rounded-kw-md p-2 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
        >
          <MessageCircle className="size-5" />
          {chatUnread > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-kw-full bg-kw-danger-text px-1 text-[10px] font-semibold leading-4 text-white">
              {chatUnread > 9 ? '9+' : chatUnread}
            </span>
          ) : null}
        </button>
        <div className="relative" ref={inboxRef}>
          <button
            type="button"
            aria-label={bellUnread > 0 ? 'Notifications, unread' : 'Notifications'}
            aria-expanded={inboxOpen}
            onClick={() => {
              setInboxOpen((open) => !open);
            }}
            className="relative rounded-kw-md p-2 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
          >
            <Bell className="size-5" />
            {bellUnread > 0 ? (
              <span className="absolute right-1.5 top-1.5 size-2 rounded-kw-full bg-kw-danger-text" />
            ) : null}
          </button>
          {inboxOpen ? (
            <div className="absolute right-0 top-11 z-30 animate-fade-in">
              <NotificationInbox
                onNavigate={() => {
                  setInboxOpen(false);
                }}
              />
              <div className="-mt-px flex items-center justify-between rounded-b-kw-lg border border-t-0 border-kw-border bg-white px-4 py-2 text-[12px]">
                <Link
                  to="/communication"
                  onClick={() => {
                    setInboxOpen(false);
                  }}
                  className="font-medium text-kw-cerulean hover:underline"
                >
                  Announcements{announcementUnread > 0 ? ` (${String(announcementUnread)})` : ''}
                </Link>
                <Link
                  to="/notifications"
                  onClick={() => {
                    setInboxOpen(false);
                  }}
                  className="font-medium text-kw-cerulean hover:underline"
                >
                  All notifications
                </Link>
              </div>
            </div>
          ) : null}
        </div>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            className="grid size-8 place-items-center rounded-kw-full bg-kw-cerulean text-[13px] font-medium text-white"
          >
            {initials}
          </button>
          {menuOpen && state.status === 'authenticated' && (
            <div className="absolute right-0 top-10 w-56 animate-fade-in rounded-kw-md border border-kw-border bg-white shadow-kw-md">
              <div className="border-b border-kw-border px-4 py-3">
                <p className="text-[13px] font-medium text-kw-shark">
                  {state.me.user.firstName} {state.me.user.lastName}
                </p>
                <p className="text-[11px] text-kw-muted">{state.me.user.email}</p>
                {(state.me.memberships ?? []).length > 1 ? (
                  <div className="-ml-1.5 mt-1.5">
                    <OrgSwitcher />
                  </div>
                ) : (
                  <p className="mt-1 text-[11px] text-kw-muted">
                    {state.me.organization.name} ·{' '}
                    <span className="capitalize">{state.me.membership.role.replace('_', ' ')}</span>
                  </p>
                )}
              </div>
              <Link
                to="/settings/profile"
                onClick={() => {
                  setMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] text-kw-shark hover:bg-kw-bg"
              >
                <UserRound className="size-4" />
                Profile settings
              </Link>
              <button
                type="button"
                onClick={() => {
                  void signOut();
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] text-kw-shark hover:bg-kw-bg"
              >
                <LogOut className="size-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
